import { Button, CircularProgress, Collapse, Container } from "@mui/material";
import { IDetectedBarcode, Scanner } from "@yudiel/react-qr-scanner";
import { useContext, useMemo, useState } from "react";
import ActivationFormContext from "../../../store/activation-form-context";
import usePreferredCamera from "../../../hooks/usePreferredCamera";
import Title from "../../../components/Title";

interface Props {
  onShowForm: () => void;
}

const ScanQrCode = ({ onShowForm: showFormHandler }: Props) => {
  const [userOpenedQRScanner, setUserOpenedQRScanner] = useState(false);
  const { qrDecodeHandler } = useContext(ActivationFormContext);
  const cameraId = usePreferredCamera();

  const decodeHandler = (e: IDetectedBarcode[]) => {
    qrDecodeHandler((e[0]?.rawValue || "").slice(0, 15));
    showFormHandler();
  };

  const showQRScanner = useMemo(() => cameraId !== undefined && userOpenedQRScanner, [cameraId, userOpenedQRScanner]);

  const getPrimaryButtonContent = () => {
    if (!userOpenedQRScanner) return "Scan now";

    if (userOpenedQRScanner && !showQRScanner) return <CircularProgress color="inherit" size={35} />;

    return "Close QR scanner";
  };

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        px: "0px",
        overflowY: "scroll",
        alignItems: "center",
      }}
    >
      <div style={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <div style={{ marginBottom: "30px" }}>
          <Title sx={{ fontSize: "30px", lineHeight: "35px" }} center>
            Scan the QR code on the back of the watch.
          </Title>
        </div>
        <div style={{ marginBottom: "30px", maxWidth: "400px", marginLeft: "auto", marginRight: "auto" }}>
          <Collapse in={!showQRScanner} {...{ timeout: 200 }}>
            <img src="/activationbg-mobile.webp" alt="cosmo watch with qr code" width="100%" />
          </Collapse>
          {cameraId !== undefined && (
            <Collapse in={showQRScanner} {...{ timeout: 200 }}>
              <Scanner
                formats={["qr_code", "micro_qr_code", "rm_qr_code"]}
                onScan={decodeHandler}
                onError={(e: any) => console.error(e)}
                styles={{
                  container: { borderRadius: 20, overflow: "hidden" },
                  video: { borderRadius: 20 },
                }}
                constraints={{
                  deviceId: cameraId ?? undefined,
                  facingMode: { ideal: "environment" },
                  advanced: [
                    {
                      facingMode: { ideal: "environment" },
                    },
                  ],
                }}
                allowMultiple
                components={{
                  torch: false,
                  zoom: false,
                }}
              />
            </Collapse>
          )}
        </div>
        <div style={{ marginBottom: "0" }}>
          <Button
            variant="contained"
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "1.4",
              width: "100%",
              boxShadow: "none",
              mb: "15px",
              "&.Mui-disabled": {
                backgroundColor: "#0050E6",
                color: "rgb(255, 255, 255)",
              },
            }}
            onClick={() => setUserOpenedQRScanner((prev) => !prev)}
            disabled={userOpenedQRScanner && !showQRScanner}
          >
            {getPrimaryButtonContent()}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "1.4",
              width: "100%",
              boxShadow: "none",
              borderWidth: "2px",
              marginBottom: 3,
            }}
            onClick={showFormHandler}
          >
            Enter code manually
          </Button>
        </div>
      </div>
    </Container>
  );
};
export default ScanQrCode;
